import { IssueTaskCategory, IssueTaskDoc, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Badge } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { issueTaskCategoryKr } from 'src/constants/issue.constants';

import { loadGridState } from 'src/utils/ag-grid-util';
import { formatDate } from 'src/utils/date-util';
import {
  createCreatorCellRenderer,
  createProductsCellRenderer,
  createTaskCategoryCellRenderer,
} from 'src/utils/issue/issue-ag-grid-renderers';
import {
  createAmountColumnGroup,
  externalServiceCellStyle,
  externalServiceCostValueGetter,
  externalServiceNameValueGetter,
  formatAmount,
  partnerCellStyle,
  productsFilterParams,
  registeredAtValueFormatter,
  storeNameTooltipValueGetter,
  taskCategoryColorMap,
  taskFinishedAtValueFormatter,
  taskFinishedAtValueGetter,
  taskPartnerNameValueGetter,
} from 'src/utils/issue/issue-ag-grid-util';
import { IssuePanelModalData, issueTaskStatusBadgeMap } from 'src/utils/issue/issue-util';

import Loading from 'src/components/Loading/Loading';

import IssuePanelModal from '../IssuePanelModal';

interface IssueTaskHistoryTableProps {
  rowData: IssueTaskDoc[];
  isLoading: boolean;
  onGridRefChange?: (gridRef: AgGridReact<IssueTaskDoc> | null) => void;
  rowHeight?: number;
  headerHeight?: number;
  groupHeaderHeight?: number;
}

export const ISSUE_TASK_HISTORY_TABLE_ID = 'issueTaskHistoryTable';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const taskCategoryValueFormatter = (params: ValueFormatterParams<IssueTaskDoc, IssueTaskCategory>) => {
  if (!params.value) return '-';
  return issueTaskCategoryKr[params.value] || params.value;
};

const issueTaskStatusValueFormatter = (params: ValueFormatterParams<IssueTaskDoc, IssueTaskStatus>) => {
  if (!params.value) return null;
  if (!issueTaskStatusBadgeMap[params.value]) return String(params.value);
  return issueTaskStatusBadgeMap[params.value].text;
};

const IssueTaskHistoryTable: FC<IssueTaskHistoryTableProps> = ({
  rowData,
  isLoading,
  onGridRefChange,
  rowHeight = 36,
  headerHeight,
  groupHeaderHeight,
}) => {
  const [gridRef, setGridRef] = useState<AgGridReact<IssueTaskDoc> | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IssuePanelModalData>(null);

  // gridRef 변경 시 부모에게 전달
  const handleGridRef = useCallback(
    (ref: AgGridReact<IssueTaskDoc> | null) => {
      setGridRef(ref);
      onGridRefChange?.(ref);
    },
    [onGridRefChange]
  );

  const handleOpenDetail = useCallback((issueCaseId: string) => {
    setModalData({ standaloneIssueId: issueCaseId });
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  const CategoryCellRenderer = useMemo(
    () => createTaskCategoryCellRenderer<IssueTaskDoc>({ showColor: false, onOpenDetail: handleOpenDetail }),
    [handleOpenDetail]
  );

  /**
   * Task 정보 요약 렌더러 (카테고리 + 상태)
   */
  const TaskSummaryCellRenderer = useCallback(
    (params: ICellRendererParams<IssueTaskDoc>) => {
      const row = params.data;
      if (!row) return '-';

      const { category, status, issueCaseId } = row;
      if (!category && !status) return '-';

      const categoryText = category ? issueTaskCategoryKr[category] || category : '';
      const categoryColor = category ? taskCategoryColorMap[category] : undefined;
      const statusBadgeInfo = status ? issueTaskStatusBadgeMap[status] : null;

      const handleCategoryClick = () => {
        if (issueCaseId) {
          handleOpenDetail(issueCaseId);
        }
      };

      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {categoryText && (
            <span
              style={{
                color: categoryColor,
                fontWeight: 500,
                cursor: issueCaseId ? 'pointer' : 'default',
                textDecoration: issueCaseId ? 'underline' : 'none',
              }}
              onClick={handleCategoryClick}>
              {categoryText}
            </span>
          )}
          {statusBadgeInfo && <Badge status={statusBadgeInfo.status} text={statusBadgeInfo.text} />}
        </span>
      );
    },
    [handleOpenDetail]
  );

  /**
   * Task 상태 셀 렌더러
   */
  const TaskStatusCellRenderer = useCallback((params: ICellRendererParams<IssueTaskDoc>) => {
    const status = params.data?.status;
    if (!status) return '-';
    const badgeInfo = issueTaskStatusBadgeMap[status];
    if (!badgeInfo) return String(status);
    return <Badge status={badgeInfo.status} text={badgeInfo.text} />;
  }, []);

  const columnDefs = useMemo<(ColDef<IssueTaskDoc> | ColGroupDef<IssueTaskDoc>)[]>(() => {
    return [
      // Task 정보 그룹
      {
        headerName: 'Task 정보',
        pinned: 'left',
        children: [
          {
            colId: 'taskSummary',
            headerName: '요약',
            width: 180,
            columnGroupShow: 'closed',
            cellRenderer: TaskSummaryCellRenderer,
            pinned: 'left',
            filter: false,
            sortable: false,
          },
          {
            field: 'category',
            headerName: '카테고리',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: CategoryCellRenderer,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: taskCategoryValueFormatter,
              convertValuesToStrings: true,
            },
            enableRowGroup: true,
          },
          {
            field: 'status',
            headerName: '상태',
            width: 120,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: TaskStatusCellRenderer,
            filterParams: {
              valueFormatter: issueTaskStatusValueFormatter,
            },
            enableRowGroup: true,
          },
          {
            field: '_id',
            headerName: 'Task ID',
            width: 140,
            pinned: 'left',
            columnGroupShow: 'open',
            enableRowGroup: true,
          },
          {
            field: 'issueCaseId',
            headerName: 'Case ID',
            width: 140,
            pinned: 'left',
            columnGroupShow: 'open',
            enableRowGroup: true,
          },
        ],
      },
      {
        headerName: '일시',
        children: [
          {
            field: 'targetDate',
            headerName: '적용 날짜',
            width: 110,
            valueFormatter: (params: ValueFormatterParams) => (params.value ? formatDate(params.value, 'MM.dd') : '-'),
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 120,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
          {
            field: 'finishedAt',
            headerName: '완료일시',
            width: 120,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
        ],
      },
      {
        field: 'storeName',
        headerName: '매장',
        width: 150,
        tooltipValueGetter: storeNameTooltipValueGetter,
      },
      {
        headerName: '처리 상품',
        width: 200,
        cellRenderer: createProductsCellRenderer<IssueTaskDoc>(),
        filter: 'agSetColumnFilter',
        filterParams: productsFilterParams,
      },
      createAmountColumnGroup(),
      {
        headerName: '파트너',
        children: [
          {
            headerName: '담당자',
            width: 100,
            valueGetter: taskPartnerNameValueGetter,
            cellStyle: partnerCellStyle,
          },
          {
            headerName: '완료시각',
            width: 140,
            columnGroupShow: 'open',
            valueGetter: taskFinishedAtValueGetter,
            valueFormatter: taskFinishedAtValueFormatter,
            cellStyle: partnerCellStyle,
          },
        ],
      },
      {
        headerName: '외부 서비스',
        children: [
          {
            headerName: '서비스',
            width: 100,
            valueGetter: externalServiceNameValueGetter,
            cellStyle: externalServiceCellStyle,
          },
          {
            headerName: '비용',
            width: 100,
            columnGroupShow: 'open',
            valueGetter: externalServiceCostValueGetter,
            valueFormatter: (params: ValueFormatterParams) => formatAmount(params.value),
            cellStyle: externalServiceCellStyle,
          },
        ],
      },
      {
        field: 'publicNote',
        headerName: '메모(공개)',
        width: 150,
      },
      {
        field: 'privateNote',
        headerName: '메모(비공개)',
        width: 150,
      },
      {
        headerName: '작성자',
        field: 'creatorName',
        width: 100,
        cellRenderer: createCreatorCellRenderer<IssueTaskDoc>(),
      },
    ];
  }, [CategoryCellRenderer, TaskSummaryCellRenderer, TaskStatusCellRenderer]);

  return (
    <>
      <div className='ag-theme-alpine height100'>
        {isLoading && <Loading title='데이터를 불러오고 있습니다.' />}
        <AgGridReact<IssueTaskDoc>
          ref={handleGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          groupHeaderHeight={groupHeaderHeight}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          onGridReady={() => loadGridState(gridRef, ISSUE_TASK_HISTORY_TABLE_ID)}
        />
      </div>
      {modalData && <IssuePanelModal open={modalOpen} onClose={handleCloseModal} {...modalData} />}
    </>
  );
};

export default IssueTaskHistoryTable;
